import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { Translate, I18n } from 'react-redux-i18n';
import { Eye, EyeOff, BarChart3, BarChart2 } from 'lucide-react';
import BasePage from '../../../components/common/basePageView';
import Spinner from '../../../components/common/spinner';
import { usePageTitle } from '../../../hooks';
import { EntityActionsDropdown } from '../../../components/common/EntityActionsDropdown/EntityActionsDropdown';
import type { EntityActionsMenuItem } from '../../../components/common/EntityActionsDropdown/EntityActionsDropdown';
import config from '../../../constants';
import { FranchiseSnapshotModel, FranchiseSnapshotResponse, MetricKey } from './types';
import { hasMixedCurrencies, getUniqueCurrencies } from './utils';
import { CurrencyWarning } from './components/CurrencyWarning';
import { FranchiseMetrics } from './components/FranchiseMetrics';
import { EditionsList } from './components/EditionsList';
import './FranchiseHub.css';

interface FranchiseHubProps {
  isSystemAdmin: boolean;
  isOrganizationAdmin: boolean;
  isCampaignAdmin: boolean;
  isEventAdmin: boolean;
  isEventOrganizerAdmin: boolean;
  isCampaignOrganizerAdmin: boolean;
}

const FranchiseHub: React.FC<FranchiseHubProps> = ({
  isSystemAdmin,
  isOrganizationAdmin,
  isCampaignAdmin,
  isEventAdmin,
  isEventOrganizerAdmin,
  isCampaignOrganizerAdmin
}) => {
  const { franchiseId } = useParams<{ franchiseId: string }>();
  const [franchise, setFranchise] = useState<FranchiseSnapshotModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [includeOffline, setIncludeOffline] = useState(true);
  const [showAllMetrics, setShowAllMetrics] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<MetricKey | null>(null);

  useEffect(() => {
    if (franchiseId) {
      loadFranchise();
    }
  }, [franchiseId]);

  const loadFranchise = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const session = JSON.parse(localStorage.getItem('session') || '{}');
      const response = await fetch(
        `${config.baseApiHost}/api/v2/franchise/${franchiseId}/snapshot`,
        {
          method: 'GET',
          headers: {
            'ApiKey': config.apikey || '',
            'Authorization': `bearer ${session.key}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: FranchiseSnapshotResponse = await response.json();

      if (result.success && result.data) {
        setFranchise(result.data);
      } else {
        setError(result.error || 'Failed to load franchise data');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOfflineToggle = () => {
    setIsAnimating(true);
    setIncludeOffline(!includeOffline);
    // Reset animation after animation completes
    setTimeout(() => setIsAnimating(false), 1000);
  };

  const handleMetricSelect = (metric: MetricKey) => {
    // Toggle selection: if same metric clicked, deselect it
    setSelectedMetric(prevMetric => prevMetric === metric ? null : metric);
  };

  // Set page title based on franchise name
  const pageTitle = franchise?.name ? `${franchise.name} - Hub` : 'Franchise Hub';
  usePageTitle(pageTitle);

  // Check access (same roles as specified)
  const hasAccess =
    isSystemAdmin ||
    Boolean(
      isOrganizationAdmin ||
        isCampaignAdmin ||
        isEventAdmin ||
        isEventOrganizerAdmin ||
        isCampaignOrganizerAdmin
    );

  if (!hasAccess) {
    return (
      <BasePage>
        <div className="franchise-hub-error">
          <Translate value="franchise.hub.error.access-denied" />
        </div>
      </BasePage>
    );
  }

  if (isLoading) {
    return (
      <BasePage>
        <div className="franchise-hub-loading">
          <Spinner />
          <p>
            <Translate value="franchise.hub.loading" />
          </p>
        </div>
      </BasePage>
    );
  }

  if (error || !franchise) {
    return (
      <BasePage>
        <div className="franchise-hub-empty">
          <Translate value="franchise.hub.error.no-data" />
        </div>
      </BasePage>
    );
  }

  const currencies = getUniqueCurrencies(franchise);
  const mixedCurrencies = hasMixedCurrencies(franchise);

  // Build action menu items
  const actionMenuItems: EntityActionsMenuItem[] = [
    {
      key: 'toggle-offline',
      label: includeOffline
        ? I18n.t('franchise.hub.controls.hide-offline')
        : I18n.t('franchise.hub.controls.include-offline'),
      icon: includeOffline ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />,
      onSelect: handleOfflineToggle
    },
    {
      key: 'toggle-metrics',
      label: showAllMetrics
        ? I18n.t('franchise.hub.controls.show-key-metrics')
        : I18n.t('franchise.hub.controls.show-all-metrics'),
      icon: showAllMetrics ? <BarChart2 aria-hidden="true" /> : <BarChart3 aria-hidden="true" />,
      onSelect: () => setShowAllMetrics(!showAllMetrics)
    }
  ];

  return (
    <BasePage title={`${franchise.name} Hub`}>
      <div className="franchise-hub">
        {/* Header with controls */}
        <div className="franchise-hub-header">
          <h1 className="franchise-hub-title">{franchise.name}</h1>

          <div className="franchise-hub-controls">
            <EntityActionsDropdown
              triggerLabel={I18n.t('franchise.hub.controls.actions')}
              menuItems={actionMenuItems}
            />
          </div>
        </div>

        {/* Currency Warning */}
        {mixedCurrencies && <CurrencyWarning currencies={currencies} />}

        {/* Franchise-level Metrics */}
        <FranchiseMetrics
          franchise={franchise}
          includeOffline={includeOffline}
          isSystemAdmin={isSystemAdmin}
          showAllMetrics={showAllMetrics}
          isAnimating={isAnimating}
          selectedMetric={selectedMetric}
          onMetricSelect={handleMetricSelect}
        />

        {/* Editions List */}
        <EditionsList
          franchise={franchise}
          includeOffline={includeOffline}
          showAllMetrics={showAllMetrics}
          isSystemAdmin={isSystemAdmin}
          isAnimating={isAnimating}
          selectedMetric={selectedMetric}
        />
      </div>
    </BasePage>
  );
};

// Redux connection
const mapStateToProps = (state: any) => ({
  isSystemAdmin: state.session.isSystemAdmin || false,
  isOrganizationAdmin: state.session.isOrganizationAdmin || false,
  isCampaignAdmin: state.session.isCampaignAdmin || false,
  isEventAdmin: state.session.isEventAdmin || false,
  isEventOrganizerAdmin: state.session.isEventOrganizerAdmin || false,
  isCampaignOrganizerAdmin: state.session.isCampaignOrganizerAdmin || false
});

export default connect(mapStateToProps)(FranchiseHub);
